import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import {
  Event,
  EventType,
  EventResponse,
  EventComment,
  EventResponseSummary,
  AttendanceStatus,
  ExcuseLetterRequest,
  getEventTypeLabel,
  getAttendanceStatusLabel,
  eventGroupLabel,
} from '../../types/event';
import { eventsAPI, schoolsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { isStaff, isAdmin, isPlayer, isParent, isGuestOnly } from '../../types/auth';
import EventResponsePanel from './EventResponsePanel';
import CommentSection from './CommentSection';
import { formatVenueSections, parseVenueSections } from '../../utils/venueSections';
import PitchGraphic from '../venues/PitchGraphic';
import TrainingPlanSection from '../trainings/TrainingPlanSection';
import PresenceSection from '../trainings/PresenceSection';
import TrainingFeedbackSection from './TrainingFeedbackSection';
import TravelSection from '../travel/TravelSection';
import EventBoardPosts from '../board/EventBoardPosts';
import { getErrorMessage } from '../../utils/errorMessage';

interface EventDetailModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onResponseChange?: () => void;
  onOpenMatch?: () => void;
  /** A kártyán mutatott hozzászólás-számláló frissítéséhez */
  onCommentCountChange?: (eventId: number, count: number) => void;
  /** Az edzés utáni értesítésből nyitva: rögtön a visszajelzéshez görget. */
  focusFeedback?: boolean;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onResponseChange,
  onOpenMatch,
  onCommentCountChange,
  focusFeedback = false,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [responses, setResponses] = useState<EventResponse[]>([]);
  const [comments, setComments] = useState<EventComment[]>([]);
  const [responseSummary, setResponseSummary] = useState<EventResponseSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [excuseLetterRequests, setExcuseLetterRequests] = useState<ExcuseLetterRequest[]>([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [schoolSending, setSchoolSending] = useState(false);
  const [reminderSending, setReminderSending] = useState<'all' | 'nonResponders' | null>(null);
  // "Nem jön" után a játékos az utazásból is kikerül: az utazás panel újratölt
  const [travelRefresh, setTravelRefresh] = useState(0);

  const eventId = event?.id;
  const excuseLetterEnabled = event?.excuseLetterEnabled;

  const loadEventDetails = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    try {
      const [responsesData, commentsData, summaryData] = await Promise.all([
        eventsAPI.getResponses(eventId),
        eventsAPI.getComments(eventId),
        eventsAPI.getResponseSummary(eventId),
      ]);

      setResponses(responsesData);
      setComments(commentsData);
      setResponseSummary(summaryData);
      onCommentCountChange?.(eventId, commentsData.length);

    } catch (err) {
      console.error('Error loading event details:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, onCommentCountChange]);

  const loadExcuseLetterRequests = useCallback(async () => {
    if (!eventId) return;
    try {
      const requests = await eventsAPI.getExcuseLetterRequests(eventId);
      setExcuseLetterRequests(requests);
    } catch (err) {
      console.error('Error loading excuse letter requests:', err);
    }
  }, [eventId]);

  useEffect(() => {
    if (isOpen && eventId) {
      loadEventDetails();
      if (isStaff(user) && excuseLetterEnabled) {
        loadExcuseLetterRequests();
      }
    }
  }, [
    isOpen,
    eventId,
    user,
    excuseLetterEnabled,
    loadEventDetails,
    loadExcuseLetterRequests,
  ]);

  const handleAddComment = async (content: string) => {
    if (!event) return;
    await eventsAPI.createComment(event.id, { content });
    await loadEventDetails();
  };

  const handleUpdateComment = async (commentId: number, content: string) => {
    if (!event) return;
    await eventsAPI.updateComment(event.id, commentId, { content });
    await loadEventDetails();
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!event) return;
    await eventsAPI.deleteComment(event.id, commentId);
    await loadEventDetails();
  };

  const handleGenerateExcuseLetters = async () => {
    if (!event) return;

    setGenerateLoading(true);
    try {
      await eventsAPI.generateExcuseLetters(event.id);
      await loadExcuseLetterRequests();
      showToast('Kikérők sikeresen legenerálva!');
    } catch (err: any) {
      console.error('Error generating excuse letters:', err);
      showToast(getErrorMessage(err, 'Hiba történt a kikérők generálásakor'), 'error');
    } finally {
      setGenerateLoading(false);
    }
  };

  /** Az elkészült, még ki nem küldött kikérők a játékosok iskoláinak. */
  const handleSendExcuseLettersToSchools = async () => {
    if (!event) return;

    setSchoolSending(true);
    try {
      const result = await schoolsAPI.sendEventExcuseLetters(event.id);
      await loadExcuseLetterRequests();
      const parts = [`${result.sentCount} kikérő elküldve az iskoláknak.`];
      if (result.skipped.length > 0) parts.push(`Kimaradt: ${result.skipped.join('; ')}.`);
      if (result.failedCount > 0) parts.push(`${result.failedCount} küldése nem sikerült.`);
      showToast(parts.join(' '), result.skipped.length > 0 || result.failedCount > 0 ? 'error' : 'success');
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A kikérőket nem sikerült elküldeni.'), 'error');
    } finally {
      setSchoolSending(false);
    }
  };

  const handleSendReminder = async (onlyNonResponders: boolean) => {
    if (!event) return;

    const question = onlyNonResponders
      ? 'Push emlékeztetőt küldesz azoknak, akik még nem jeleztek vissza. Mehet?'
      : 'Push emlékeztetőt küldesz az esemény összes játékosának (aki nem mondott nemet). Mehet?';
    if (!window.confirm(question)) return;

    setReminderSending(onlyNonResponders ? 'nonResponders' : 'all');
    try {
      const result = await eventsAPI.sendReminder(event.id, onlyNonResponders);
      if (result.pushNotConfigured) {
        showToast('A push értesítés nincs beállítva a szerveren, az emlékeztető nem ment ki.', 'error');
      } else if (result.playerCount === 0) {
        showToast(onlyNonResponders ? 'Mindenki visszajelzett, nincs kit emlékeztetni.' : 'Nincs kit emlékeztetni.');
      } else if (result.userCount === 0) {
        showToast(`${result.playerCount} játékos érintett, de egyikhez sincs aktív fiók.`, 'error');
      } else {
        showToast(
          `Emlékeztető elküldve: ${result.playerCount} játékos, ${result.userCount} fiók` +
            (result.delivered === 0 ? ' (egyik fióknál sincs engedélyezve a push értesítés)' : '')
        );
      }
    } catch (err: any) {
      console.error('Error sending reminder:', err);
      showToast(getErrorMessage(err, 'Hiba történt az emlékeztető küldésekor'), 'error');
    } finally {
      setReminderSending(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
  };

  const getEventTypeColor = (type: EventType) => {
    switch (type) {
      case EventType.Training:
        return 'bg-blue-100 text-blue-800';
      case EventType.Match:
        return 'bg-red-100 text-red-800';
      case EventType.Meeting:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: AttendanceStatus) => {
    switch (status) {
      case AttendanceStatus.Attending:
        return 'text-green-600';
      case AttendanceStatus.NotAttending:
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  const isPast = event ? new Date(event.endTime) < new Date() : false;
  const canModify = isStaff(user) && (isAdmin(user) || event?.createdByUserId === user?.id);
  // A vendég az esemény alapadatait látja: ki, mikor, melyik pályán. A
  // résztvevők és a hozzászólások személyes adatok - azokhoz nincs köze.
  const guestView = isGuestOnly(user);
  const canRespond = isPlayer(user) || isParent(user);
  const respondHere = event?.responsesEnabled === true && !isPast && !event?.isCancelled && canRespond;

  if (!event) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-w-2xl md:rounded-lg md:max-h-[90vh]">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between p-4 md:p-6 border-b">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getEventTypeColor(event.type)}`}>
                {getEventTypeLabel(event.type)}
              </span>
              {eventGroupLabel(event) && (
                <span className="px-3 py-1 text-sm font-medium bg-primary-100 text-primary-800 rounded-full">
                  {eventGroupLabel(event)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canModify && (
                <>
                  <button
                    onClick={onEdit}
                    aria-label="Szerkesztés"
                    title="Szerkesztés"
                    className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={onDelete}
                    aria-label="Törlés"
                    title="Törlés"
                    className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-full"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                aria-label="Bezárás"
                className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-600 rounded-full"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 pb-safe-6">
            <Dialog.Title className="text-2xl font-bold text-gray-900 mb-2">
              {event.title}
            </Dialog.Title>

            {event.description && (
              <p className="text-gray-600 mb-6">{event.description}</p>
            )}

            {/* A felállás és a statisztika a stábé - a szülő/játékos nem látja */}
            {event.type === EventType.Match && onOpenMatch && isStaff(user) && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={onOpenMatch}
                  className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 font-medium flex items-center gap-2"
                >
                  <UserGroupIcon className="h-5 w-5" />
                  Mérkőzés – Felállás és statisztika
                </button>
              </div>
            )}

            {/* Event details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 text-gray-600">
                <CalendarIcon className="h-5 w-5" />
                <span>{formatDate(event.startTime)}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <ClockIcon className="h-5 w-5" />
                <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
              </div>
              {(event.location || event.venue) && (
                <div className="flex items-center gap-3 text-gray-600 md:col-span-2">
                  <MapPinIcon className="h-5 w-5" />
                  <span>{event.venue?.name || event.location}{isStaff(user) && formatVenueSections(event.venueSections, event.venue?.sectionNames) ? ` (${formatVenueSections(event.venueSections, event.venue?.sectionNames)})` : ''}</span>
                  {event.venue?.address && (
                    <span className="text-gray-400">({event.venue.address})</span>
                  )}
                </div>
              )}
              {/* Pályarajz: melyik részt foglalja az esemény - csak a stábnak */}
              {isStaff(user) && event.venue && (event.venue.sections ?? 1) > 1 && (
                <div className="md:col-span-2 max-w-sm">
                  <PitchGraphic
                    sectionCount={event.venue.sections as number}
                    highlighted={parseVenueSections(event.venueSections)}
                    sectionNames={event.venue.sectionNames}
                  />
                </div>
              )}
            </div>

            {/* Gyerekenként: jön-e, hogyan jut el, kikérő - a szülőnek / játékosnak legfelül */}
            {respondHere && (
              <div className="mb-6 rounded-lg bg-gray-50 p-4">
                <h4 className="mb-3 text-sm font-semibold text-gray-900">A te válaszod</h4>
                <EventResponsePanel
                  eventId={event.id}
                  excuseLetterEnabled={event.excuseLetterEnabled}
                  travelRefreshKey={travelRefresh}
                  onTravelChanged={() => setTravelRefresh((n) => n + 1)}
                  onStatusChange={() => {
                    loadEventDetails();
                    onResponseChange?.();
                  }}
                />
              </div>
            )}

            {/* Utazás: ki melyik járművel, honnan - a vendéghez nem tartozik */}
            {!guestView && (
              <TravelSection
                eventId={event.id}
                refreshKey={travelRefresh}
                respondElsewhere={respondHere}
                onChanged={() => setTravelRefresh((n) => n + 1)}
              />
            )}

            {/* Edzésterv - csak a stábnak */}
            {event.type === EventType.Training && isStaff(user) && (
              <TrainingPlanSection
                eventId={event.id}
                canEdit={isStaff(user)}
                ageGroupId={event.ageGroupId}
                durationMinutes={Math.round((new Date(event.endTime).getTime() - new Date(event.startTime).getTime()) / 60000)}
              />
            )}

            {/* Edzés utáni visszajelzés: a játékos/szülő kitölti, az edző az összesítőt látja */}
            {event.type === EventType.Training && !guestView && (
              <TrainingFeedbackSection
                eventId={event.id}
                startTime={event.startTime}
                staffView={isStaff(user)}
                autoFocus={focusFeedback}
              />
            )}

            {/* Excuse Letter Management - only for staff when enabled */}
            {event.excuseLetterEnabled && isStaff(user) && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                    <h4 className="text-sm font-semibold text-gray-900">
                      Kikérő kérelmek ({excuseLetterRequests.length})
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {excuseLetterRequests.filter(r => !r.isGenerated).length > 0 && (
                      <button
                        onClick={handleGenerateExcuseLetters}
                        disabled={generateLoading}
                        className="px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {generateLoading ? 'Generálás...' : `Kikérők generálása (${excuseLetterRequests.filter(r => !r.isGenerated).length})`}
                      </button>
                    )}
                    {excuseLetterRequests.filter(r => r.isGenerated && !r.sentToSchoolAt).length > 0 && (
                      <button
                        onClick={handleSendExcuseLettersToSchools}
                        disabled={schoolSending}
                        className="px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 disabled:opacity-50 text-sm font-medium"
                      >
                        {schoolSending
                          ? 'Küldés...'
                          : `Küldés az iskoláknak (${excuseLetterRequests.filter(r => r.isGenerated && !r.sentToSchoolAt).length})`}
                      </button>
                    )}
                  </div>
                </div>
                {excuseLetterRequests.length === 0 ? (
                  <p className="text-sm text-gray-500">Még nincs kikérő kérelem.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {excuseLetterRequests.map((request) => (
                      <div
                        key={request.documentId}
                        className="flex items-center justify-between py-2 px-3 bg-white rounded"
                      >
                        <span className="text-sm font-medium text-gray-900">{request.playerName}</span>
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          request.sentToSchoolAt
                            ? 'bg-blue-100 text-blue-700'
                            : request.isGenerated
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {request.sentToSchoolAt ? 'Iskolának elküldve' : request.isGenerated ? 'Legenerálva' : 'Függőben'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Kézi push emlékeztető - csak stábnak, jövőbeli eseménynél */}
            {isStaff(user) && !isPast && (
              <div className="mb-6 p-4 bg-primary-50 border border-primary-100 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <BellAlertIcon className="h-5 w-5 text-primary-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Emlékeztető küldése</h4>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => handleSendReminder(false)}
                    disabled={reminderSending !== null}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {reminderSending === 'all' ? 'Küldés...' : 'Emlékeztető mindenkinek'}
                  </button>
                  {event.responsesEnabled && (
                    <button
                      type="button"
                      onClick={() => handleSendReminder(true)}
                      disabled={reminderSending !== null}
                      className="px-4 py-2 bg-white text-primary-700 border border-primary-300 rounded-md hover:bg-primary-100 disabled:opacity-50 text-sm font-medium"
                    >
                      {reminderSending === 'nonResponders' ? 'Küldés...' : 'Emlékeztető a nem válaszolóknak'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Response summary */}
            {responseSummary && event.responsesEnabled && !guestView && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <UserGroupIcon className="h-5 w-5 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    Visszajelzések ({responseSummary.totalCount})
                  </h4>
                </div>
                <div className="flex gap-6 text-sm mb-4">
                  <span className="text-green-600 font-medium">
                    {responseSummary.attendingCount} jön
                  </span>
                  <span className="text-red-600 font-medium">
                    {responseSummary.notAttendingCount} nem jön
                  </span>
                  <span className="text-gray-500">
                    {responseSummary.noResponseCount} nem válaszolt
                  </span>
                </div>

                {/* Response list */}
                {responses.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {responses.map((response) => (
                      <div
                        key={response.id}
                        className="flex items-center justify-between py-2 px-3 bg-white rounded"
                      >
                        <div>
                          <span className="font-medium text-gray-900">
                            {response.playerName ?? response.userName}
                          </span>
                          {/* Ki jelzett vissza helyette (pl. a szülő) - ha nem ő maga */}
                          {response.playerName && response.userName !== response.playerName && (
                            <span className="text-gray-500 text-sm ml-2">
                              ({response.userName})
                            </span>
                          )}
                          {response.notes && (
                            <div className="text-xs text-gray-500">{response.notes}</div>
                          )}
                        </div>
                        <span className={`font-medium ${getStatusColor(response.status)}`}>
                          {getAttendanceStatusLabel(response.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Ki jelent meg ténylegesen - az edző rögzíti */}
            {event.type === EventType.Training && isStaff(user) && (
              <PresenceSection eventId={event.id} startTime={event.startTime} />
            )}

            {/* Az eseményhez kötött üzenőfal-bejegyzések */}
            {!guestView && <EventBoardPosts eventId={event.id} />}

            {/* Comments section: a vendégnek nem jelenik meg */}
            <div className={`border-t pt-6 ${guestView ? 'hidden' : ''}`}>
              <CommentSection
                comments={comments}
                onAddComment={handleAddComment}
                onUpdateComment={handleUpdateComment}
                onDeleteComment={handleDeleteComment}
                loading={loading}
              />
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default EventDetailModal;
